/**
 * Org membership helpers — single source of truth for "which orgs does this
 * user have a relationship with, and as what role?" on the client.
 *
 * Reads from the new dual-storage shape (UPDATE.md §3.6.7):
 *   - user.publicData.organizationProfiles[]   → orgs the user OWNS (branding)
 *   - user.privateData.memberOfOrganizations[] → orgs the user is a member of
 *
 * Server enforcement still lives in server/api-util/orgPermissions.js — these
 * helpers exist for UX (which orgs to show in the switcher, which actions to
 * enable in the UI). They mirror the server logic but the server is the gate.
 */

import { ROLES, isValidRole } from './orgRoles';

const ensureArray = v => (Array.isArray(v) ? v : []);

/**
 * Orgs the user owns. Each entry is the public organization profile
 * (name, logo, bio, …). Always public — these are the org's branding.
 */
export const getUserOwnedOrgs = user =>
  ensureArray(user?.attributes?.profile?.publicData?.organizationProfiles);

/**
 * Orgs the user is a non-owner member of. Each entry is:
 *   { orgId, ownerId, orgName, orgLogo, role, joinedAt }
 */
export const getUserMemberships = user =>
  ensureArray(user?.attributes?.profile?.privateData?.memberOfOrganizations);

/**
 * Does the user OWN this org?
 */
export const isOrgOwner = (user, orgId) =>
  !!orgId && getUserOwnedOrgs(user).some(o => o?.id === orgId);

/**
 * Does the user belong to this org in ANY capacity (owner or member)?
 */
export const isOrgMember = (user, orgId) => {
  if (!orgId) return false;
  if (isOrgOwner(user, orgId)) return true;
  return getUserMemberships(user).some(m => m?.orgId === orgId);
};

/**
 * The user's role in the given org, or null if they're not in it.
 * Owner state takes precedence over a stale `memberOfOrganizations` entry.
 */
export const getUserOrgRole = (user, orgId) => {
  if (!orgId) return null;
  if (isOrgOwner(user, orgId)) return ROLES.OWNER;
  const m = getUserMemberships(user).find(x => x?.orgId === orgId);
  if (m && isValidRole(m.role)) return m.role;
  return null;
};

/**
 * Returns the owner's userId for the given org, from the user's own data:
 *  - If the user is the owner, returns their own id
 *  - If the user is a member, returns the cached ownerId from membership
 *  - Otherwise null
 */
export const getOrgOwnerIdForUser = (user, orgId) => {
  if (!orgId) return null;
  if (isOrgOwner(user, orgId)) return user?.id?.uuid || user?.id || null;
  const m = getUserMemberships(user).find(x => x?.orgId === orgId);
  return m?.ownerId || null;
};

/**
 * Unified org list for switchers / menus. Returns a single array of
 * `{ id, role, isOwner, ...orgBranding }` entries, owned orgs first.
 *
 * For owned orgs we have full branding from publicData.organizationProfiles.
 * For member-of orgs we have whatever was cached on join (orgName, orgLogo)
 * — enough to render an entry in the switcher.
 */
export const getAllUserOrgs = user => {
  const owned = getUserOwnedOrgs(user).map(o => ({
    ...o,
    role: ROLES.OWNER,
    isOwner: true,
  }));
  const memberships = getUserMemberships(user).map(m => ({
    id: m.orgId,
    ownerId: m.ownerId,
    name: m.orgName,
    profileImage: m.orgLogo,
    role: m.role,
    isOwner: false,
    joinedAt: m.joinedAt,
  }));
  // Deduplicate by id (in the very unlikely case of double-bookkeeping).
  const seen = new Set();
  return [...owned, ...memberships].filter(o => {
    if (!o?.id || seen.has(o.id)) return false;
    seen.add(o.id);
    return true;
  });
};
